import { ArtifactContent } from '@/components/chat/ContentArtifact';
import { sanitizeArtifactTitle } from '@/lib/utils';

interface AgentMetadata {
  agentId: string;
  agentType: string;
  confidence: number;
  [key: string]: unknown;
}

interface DetectionResult {
  shouldCreateArtifact: boolean;
  artifactType?: ArtifactContent['type'];
  title?: string;
}

interface DetectionOptions {
  /**
   * Set when the graph service explicitly marked the run `artifactBehavior: 'persist'`.
   * The service owns followup/QA classification, so its decision overrides the
   * local QA-answer regex heuristics (which exist only for legacy messages
   * that carry no routing metadata).
   */
  skipQaHeuristics?: boolean;
}

/**
 * Determines if a message should create an artifact based on agent type and content
 */
export function detectArtifactContent(
  content: string,
  agentMetadata?: AgentMetadata,
  options?: DetectionOptions
): DetectionResult {
  if (!options?.skipQaHeuristics && isQaReviewAnswer(content)) {
    return { shouldCreateArtifact: false };
  }

  // Always check content patterns first for press releases and other distinctive content
  // This ensures press releases are correctly identified regardless of which agent created them
  const contentResult = detectContentPatterns(content);
  if (contentResult.shouldCreateArtifact) {
    return contentResult;
  }

  // Fallback to agent-based detection if no clear content patterns
  if (agentMetadata) {
    const agentResult = detectByAgent(content, agentMetadata);
    if (agentResult.shouldCreateArtifact) {
      return agentResult;
    }
  }

  return { shouldCreateArtifact: false };
}

function isQaReviewAnswer(content: string): boolean {
  const lowerContent = content.toLowerCase();
  const refersToDraft =
    /\b(draft|press release|artifact|copy|version|response|output)\b/i.test(content);
  const requestAlignmentLanguage =
    /\b(captures?|captured|original request|requested announcement details|requirements?|instructions?|brief|what (?:you|i|we) asked)\b/i.test(content);
  const reviewSections =
    /(?:^|\n)\s*(?:\*\*)?(covered|missing|to be revised|revisions?|what should be revised)(?:\*\*)?\s*:/i.test(content);
  const evaluationLead =
    /\b(largely captures|does not fully capture|partially captures|captures most|fails to capture|missing from the draft)\b/i.test(content);

  return refersToDraft && (requestAlignmentLanguage || reviewSections || evaluationLead || lowerContent.includes('verification-note requirement'));
}

/**
 * Agent-based artifact detection
 */
function detectByAgent(content: string, agentMetadata: AgentMetadata): DetectionResult {
  const { agentType } = agentMetadata;

  // Map agent types to artifact types
  const agentMappings: Record<string, { 
    type: ArtifactContent['type']; 
    minLength: number; 
    keywords?: string[];
  }> = {
    crisis: { 
      type: 'statement', 
      minLength: 100,
      keywords: ['statement', 'responds to', 'position', 'clarification']
    },
    media: { 
      type: 'media_pitch', 
      minLength: 150,
      keywords: ['pitch', 'story idea', 'exclusive', 'interview']
    },
    social: { 
      type: 'social_media', 
      minLength: 50,
      keywords: ['post', 'tweet', 'social', 'engagement']
    },
    executive: { 
      type: 'statement', 
      minLength: 100,
      keywords: ['announcement', 'statement', 'message', 'communication']
    },
    content: { 
      type: 'content', 
      minLength: 200,
      keywords: ['content', 'article', 'blog', 'copy']
    },
    internal: { 
      type: 'email', 
      minLength: 100,
      keywords: ['email', 'memo', 'internal', 'team']
    }
  };

  const mapping = agentMappings[agentType];
  if (!mapping) {
    return { shouldCreateArtifact: false };
  }

  // Check minimum length
  if (content.length < mapping.minLength) {
    return { shouldCreateArtifact: false };
  }

  // Check for specific agent IDs or content patterns
  if (mapping.keywords) {
    const hasKeywords = mapping.keywords.some(keyword => 
      content.toLowerCase().includes(keyword.toLowerCase())
    );
    
    // For agent-generated content, require BOTH keywords AND substantial length
    // AND must not be conversational
    if (hasKeywords && content.length > mapping.minLength * 3 && !isConversational(content)) {
      return {
        shouldCreateArtifact: true,
        artifactType: mapping.type,
        title: generateTitle(content, mapping.type)
      };
    }
  }

  return { shouldCreateArtifact: false };
}

/**
 * Content pattern-based detection (fallback)
 */
function detectContentPatterns(content: string): DetectionResult {
  const lowerContent = content.toLowerCase();
  
  // Press release patterns - detect actual press releases, not conversational mentions
  if (
    lowerContent.includes('for immediate release') ||
    (lowerContent.includes('press release') && content.length > 300 && !isConversational(content)) ||
    (lowerContent.includes('announces') && content.length > 400 && content.includes('\n') && !isConversational(content)) ||
    // Pattern for company announcements without explicit "press release" mention
    (content.length > 300 && !isConversational(content) && 
     lowerContent.match(/\b([a-z]+ (?:inc\.?|ltd\.?|corp\.?|company|llc))\b.*\b(?:announces?|launches?|introduces?|unveils?)\b/i) &&
     content.split('\n').length > 3)
  ) {
    return {
      shouldCreateArtifact: true,
      artifactType: 'press_release',
      title: generateTitle(content, 'press_release')
    };
  }

  // Statement patterns - must be substantial and formal
  if (
    (lowerContent.includes('official statement') && content.length > 300) ||
    (lowerContent.includes('company statement') && content.length > 300) ||
    (lowerContent.includes('responds to') && content.length > 400) ||
    (lowerContent.includes('clarification') && content.length > 300 && content.includes('\n\n'))
  ) {
    return {
      shouldCreateArtifact: true,
      artifactType: 'statement',
      title: generateTitle(content, 'statement')
    };
  }

  // Media pitch patterns
  if (
    lowerContent.includes('media pitch') ||
    lowerContent.includes('story idea') ||
    lowerContent.includes('exclusive') ||
    (lowerContent.includes('interview') && lowerContent.includes('available'))
  ) {
    return {
      shouldCreateArtifact: true,
      artifactType: 'media_pitch',
      title: generateTitle(content, 'media_pitch')
    };
  }

  // Email patterns - must be complete emails
  if (
    (lowerContent.includes('subject:') && lowerContent.includes('dear ') && content.length > 200) ||
    (lowerContent.includes('dear ') && (lowerContent.includes('best regards') || lowerContent.includes('sincerely')) && content.length > 300)
  ) {
    return {
      shouldCreateArtifact: true,
      artifactType: 'email',
      title: generateTitle(content, 'email')
    };
  }

  // Social media patterns
  if (
    content.length < 300 && (
      lowerContent.includes('#') ||
      lowerContent.includes('@') ||
      lowerContent.includes('retweet') ||
      lowerContent.includes('share this')
    )
  ) {
    return {
      shouldCreateArtifact: true,
      artifactType: 'social_media',
      title: generateTitle(content, 'social_media')
    };
  }

  // DO NOT create artifacts for generic content - only specific content types
  // This prevents every AI response from becoming an artifact
  
  return { shouldCreateArtifact: false };
}

/**
 * Generates a title for the artifact based on content and type
 */
function generateTitle(content: string, type: ArtifactContent['type']): string {
  // Special handling for press releases
  if (type === 'press_release') {
    return sanitizeArtifactTitle(extractPressReleaseTitle(content), 'Press Release');
  }
  
  // Extract potential title from first line or sentence
  const firstLine = content.split('\n')[0].trim();
  const firstSentence = content.split(/[.!?]/)[0].trim();
  
  // Use first line if it's short and looks like a title
  if (firstLine.length < 100 && firstLine.length > 10 && !firstLine.includes('FOR IMMEDIATE RELEASE')) {
    return sanitizeArtifactTitle(firstLine, getTypeLabel(type));
  }
  
  // Use first sentence if it's reasonable length
  if (firstSentence.length < 150 && firstSentence.length > 10 && !firstSentence.includes('FOR IMMEDIATE RELEASE')) {
    return sanitizeArtifactTitle(firstSentence, getTypeLabel(type));
  }
  
  // Try to extract company/subject from content
  const companyMatch = content.match(/\b([A-Z][a-zA-Z\s]+(?:Inc\.?|Ltd\.?|Corp\.?|Company|LLC))\b/);
  const announcesMatch = content.match(/\b([A-Z][a-zA-Z\s]+)\s+(?:announces?|launches?|introduces?|unveils?)\s+([a-zA-Z\s]+)/i);
  
  if (announcesMatch) {
    return sanitizeArtifactTitle(`${announcesMatch[1]} ${announcesMatch[2]}`, getTypeLabel(type));
  }
  
  if (companyMatch) {
    return sanitizeArtifactTitle(`${companyMatch[1]} ${getTypeLabel(type)}`, getTypeLabel(type));
  }
  
  // Default titles based on type
  const defaults = {
    press_release: 'Press Release',
    statement: 'Statement',
    media_pitch: 'Media Pitch',
    email: 'Email Draft',
    social_media: 'Social Media Post',
    content: 'Content Draft',
    outreach_plan: 'Outreach Plan',
    thought_leadership: 'Thought Leadership Piece'
  };
  
  return sanitizeArtifactTitle(defaults[type] || 'Content Draft', 'Content Draft');
}

/**
 * Extracts title specifically for press releases
 */
function extractPressReleaseTitle(content: string): string {
  const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  // Look for the headline after "FOR IMMEDIATE RELEASE"
  let foundHeader = false;
  for (const line of lines) {
    if (line.toUpperCase().includes('FOR IMMEDIATE RELEASE')) {
      foundHeader = true;
      continue;
    }
    
    // Skip empty lines and date lines
    if (foundHeader && line.length > 0 && !line.match(/^\d{1,2}\/\d{1,2}\/\d{4}|^[A-Z]{3,} \d{1,2}, \d{4}/)) {
      // This should be the headline
      if (line.length > 10 && line.length < 150) {
        return sanitizeArtifactTitle(line, 'Press Release');
      }
    }
  }
  
  // Fallback: look for company announcement patterns
  const announcementMatch = content.match(/\b([A-Z][a-zA-Z\s&]+(?:Inc\.?|Ltd\.?|Corp\.?|Company|LLC)?)\s+(?:announces?|launches?|introduces?|unveils?|reports?)\s+([^.]+)/i);
  if (announcementMatch) {
    const company = announcementMatch[1].trim();
    const announcement = announcementMatch[2].trim();
    return sanitizeArtifactTitle(`${company} ${announcement.charAt(0).toUpperCase() + announcement.slice(1)}`, 'Press Release');
  }
  
  // Another fallback: look for headline patterns
  const headlineMatch = content.match(/^([A-Z][^.!?]*(?:Announces?|Launches?|Introduces?|Unveils?|Reports?)[^.!?]*)/m);
  if (headlineMatch) {
    return sanitizeArtifactTitle(headlineMatch[1], 'Press Release');
  }
  
  return 'Press Release';
}

/**
 * Gets human-readable label for artifact type
 */
function getTypeLabel(type: ArtifactContent['type']): string {
  const labels = {
    press_release: 'Press Release',
    statement: 'Statement',
    media_pitch: 'Media Pitch',
    email: 'Email',
    social_media: 'Social Post',
    content: 'Content',
    outreach_plan: 'Outreach Plan',
    thought_leadership: 'Thought Leadership'
  };
  
  return labels[type] || 'Content';
}

/**
 * Determines if content is conversational (shouldn't be an artifact)
 */
function isConversational(content: string): boolean {
  const trimmedContent = content.trim();
  
  // Strong conversational starters that definitely indicate helper responses
  const strongConversationalStarters = [
    /^(sure,|okay,|yes,|i'll|i can|i've|let me|here's|here is)/i,
    /^(great!|perfect!|excellent!|absolutely,|certainly,|of course,)/i,
    /^(i've made|i've created|i've updated|i've modified|i've shortened|i've lengthened)/i,
    /^(here's a|here's the|this is a|this is the|below is)/i
  ];
  
  // Check for strong conversational starters
  if (strongConversationalStarters.some(pattern => pattern.test(trimmedContent))) {
    return true;
  }
  
  // AI assistance language patterns
  const aiAssistancePatterns = [
    /\b(as requested|as you asked|i've made it|i've adjusted|based on your request)\b/i,
    /\b(here's the revised|here's the updated|here's the modified)\b/i,
    /\b(let me know if|hope this helps|feel free to|any questions)\b/i,
    /\b(you can now|this should|this version)\b/i
  ];
  
  // Check for short responses (under 250 chars) with AI assistance language
  if (trimmedContent.length < 250 && aiAssistancePatterns.some(pattern => pattern.test(content))) {
    return true;
  }
  
  // Check for multiple question marks (Q&A style)
  if ((content.match(/\?/g) || []).length >= 2) {
    return true;
  }
  
  // Very short responses are likely conversational
  if (trimmedContent.length < 100) {
    return true;
  }
  
  return false;
}

/**
 * Checks if content is substantial enough for an artifact
 */
export function isSubstantialContent(content: string): boolean {
  if (isQaReviewAnswer(content)) {
    return false;
  }

  // First check if it's conversational - if so, never create artifact
  if (isConversational(content)) {
    return false;
  }
  
  // Remove common conversational starters and enders
  const cleanContent = content
    .replace(/^(sure,?\s*|okay,?\s*|absolutely,?\s*|here's|here is|i've)/i, '')
    .replace(/(let me know if you need any changes|hope this helps|feel free to ask|any questions)/i, '')
    .trim();
  
  // Reasonable minimum length requirement
  if (cleanContent.length < 150) {
    return false;
  }
  
  // Look for structure indicators that suggest formal content
  const hasStructureIndicators = [
    /FOR IMMEDIATE RELEASE/i,
    /^#{1,6}\s/m,  // Markdown headers
    /^\d+\.\s/m,   // Numbered lists
    /^[-*]\s/m,    // Bullet points
    /\n\n.*:\n/,   // Section headers
    /Subject:\s*/i, // Email subject
    /Dear\s+/i,    // Email greeting
    /\*\*.*\*\*/,  // Bold formatting
    /\b(?:announces?|launches?|introduces?|unveils?)\b/i, // Announcement verbs
    /\b(?:company|corporation|organization|inc\.?|ltd\.?|llc)\b/i // Company identifiers
  ].some(pattern => pattern.test(content));
  
  // Check for paragraph structure
  const hasGoodFormatting = content.includes('\n') && content.split('\n').length > 2;
  
  // Lower threshold but still substantial
  const isLongEnough = cleanContent.length > 200;
  
  // Create artifacts for content that has structure OR is long and well-formatted
  return (hasStructureIndicators && hasGoodFormatting) || 
         (isLongEnough && hasGoodFormatting && content.split('\n\n').length > 1);
}

// Update intent detection patterns
const UPDATE_INTENT_PATTERNS = [
  // Direct update requests
  /(?:make|update|change|modify|edit|revise|adjust|fix|improve|enhance)\s+(?:that|this|the|it)/i,
  /(?:can you|could you|please)\s+(?:make|update|change|modify|edit|revise|adjust|fix|improve|enhance)/i,
  
  // Specific content modifications
  /(?:make\s+(?:it|this|that)\s+(?:shorter|longer|more|less|better|clearer|stronger|weaker))/i,
  /(?:change\s+the\s+(?:tone|style|length|format|wording|headline|title|ending))/i,
  /(?:add|include|remove|delete|insert)\s+(?:a|an|the|this|that|some|more)/i,
  
  // Comparative requests
  /(?:instead\s+of|rather\s+than|replace\s+.+\s+with)/i,
  /(?:but\s+(?:make|change|use))/i,
  
  // Conditional modifications
  /(?:but|however|except)\s+(?:make|change|use|with)/i,
  
  // Version-specific requests
  /(?:previous|last|earlier)\s+(?:version|draft)/i,
  /(?:go\s+back\s+to|revert\s+to)/i,
  
  // Common edit requests
  /(?:rewrite|rephrase|reword|restructure|reorganize)/i,
  /(?:focus\s+(?:on|more\s+on)|emphasize|highlight)/i,
  /(?:shorten|lengthen|expand|elaborate|simplify)/i,
  /(?:more\s+(?:formal|casual|professional|friendly|direct|persuasive))/i,
  /(?:less\s+(?:formal|technical|wordy|verbose|aggressive))/i,
  
  // Direct commands without "the/it"
  /^(?:make|change|update|edit|revise|improve|enhance|fix)\s+/i,
  /(?:need\s+(?:to|you\s+to)|want\s+(?:to|you\s+to))\s+(?:make|change|update|edit)/i,
  
  // Additional edit patterns to catch more cases
  /(?:try\s+(?:making|changing|updating|editing))/i,
  /(?:how\s+about|what\s+if)\s+(?:you|we|i)\s+(?:make|change|update|edit)/i,
  /(?:should\s+(?:be|have|include|mention|say|state))/i,
  /(?:would\s+be\s+better\s+if)/i,
  /(?:different|alternative|another)\s+(?:approach|version|way)/i,
  /(?:cut\s+(?:down|out)|trim|reduce)/i,
  /(?:beef\s+up|flesh\s+out|expand\s+on)/i,
  /(?:tweak|adjust|modify|alter)\b/i,
  /(?:polish|refine|perfect)/i
];

const CONTENT_TYPE_KEYWORDS = {
  'press_release': ['press release', 'announcement', 'statement', 'immediate release'],
  'social_media': ['social media', 'tweet', 'post', 'facebook', 'linkedin', 'instagram'],
  'email': ['email', 'message', 'subject line'],
  'statement': ['statement', 'response', 'comment'],
  'media_pitch': ['pitch', 'story idea', 'media pitch', 'journalist'],
  'content': ['content', 'copy', 'text', 'writing']
};

export interface UpdateDetectionResult {
  isUpdateIntent: boolean;
  editReason?: string;
  targetContentType?: string;
  confidence: number;
}

export function detectUpdateIntent(
  userMessage: string, 
  _previousMessages: string[] = []
): UpdateDetectionResult {
  void _previousMessages;
  const lowerMessage = userMessage.toLowerCase();
  
  // Check for direct update patterns
  const updateMatches = UPDATE_INTENT_PATTERNS.filter(pattern => pattern.test(lowerMessage));
  
  if (updateMatches.length === 0) {
    return { isUpdateIntent: false, confidence: 0 };
  }
  
  // Determine confidence based on number of matching patterns (more lenient)
  const confidence = Math.min(updateMatches.length * 0.25 + 0.3, 1.0);
  
  // Extract edit reason from the message
  let editReason = userMessage.trim();
  if (editReason.length > 100) {
    editReason = editReason.substring(0, 97) + '...';
  }
  
  // Detect target content type
  let targetContentType: string | undefined;
  for (const [type, keywords] of Object.entries(CONTENT_TYPE_KEYWORDS)) {
    if (keywords.some(keyword => lowerMessage.includes(keyword))) {
      targetContentType = type;
      break;
    }
  }
  
  return {
    isUpdateIntent: true,
    editReason,
    targetContentType,
    confidence
  };
}

export function calculateContentSimilarity(content1: string, content2: string): number {
  // Simple similarity calculation based on:
  // 1. Common words
  // 2. Similar length
  // 3. Content structure
  
  const normalize = (text: string) => text.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  const text1 = normalize(content1);
  const text2 = normalize(content2);
  
  if (!text1 || !text2) return 0;
  
  // Length similarity (0-0.2 weight)
  const lengthDiff = Math.abs(text1.length - text2.length);
  const maxLength = Math.max(text1.length, text2.length);
  const lengthSimilarity = Math.max(0, 1 - (lengthDiff / maxLength)) * 0.2;
  
  // Word similarity (0-0.6 weight)
  const words1 = new Set(text1.split(' ').filter(w => w.length > 2));
  const words2 = new Set(text2.split(' ').filter(w => w.length > 2));
  
  const intersection = new Set([...words1].filter(w => words2.has(w)));
  const union = new Set([...words1, ...words2]);
  
  const wordSimilarity = union.size > 0 ? (intersection.size / union.size) * 0.6 : 0;
  
  // Structure similarity (0-0.2 weight) - check for similar content patterns
  const hasForImmediateRelease1 = /for\s+immediate\s+release/i.test(content1);
  const hasForImmediateRelease2 = /for\s+immediate\s+release/i.test(content2);
  const structureSimilarity = (hasForImmediateRelease1 === hasForImmediateRelease2) ? 0.2 : 0;
  
  return lengthSimilarity + wordSimilarity + structureSimilarity;
}
